import { defineStore } from 'pinia'
import { HttpStatusCode } from 'axios'
import { API } from '../../../services'
import { defaultPagination, defaultSorting } from '../../../services/defaults'
import type { Filters, Pagination } from '../../../services/types'
import { setPagination } from '../../../services/utils/utils'
import router from '../../../router'

type StockOutHistoryState = {
  records: Record<string, unknown>[]
  loading: boolean
  error: string | null
  pagination: Pagination
  sorting: { sortBy: string; sortingOrder: 'asc' | 'desc' | null }
}

export const useStockOutHistoryStore = defineStore('stockOutHistory', {
  state: (): StockOutHistoryState => ({
    records: [],
    loading: false,
    error: null,
    pagination: { ...defaultPagination },
    sorting: { ...defaultSorting, sortBy: 'created_at', sortingOrder: 'desc' },
  }),

  actions: {
    setPagination(pagination: Pagination) {
      this.pagination = setPagination(pagination)
    },

    setCurrentPage(addPage: boolean) {
      const page = this.pagination?.page ?? 1
      this.pagination = { ...this.pagination, page: addPage ? page + 1 : page - 1 }
    },

    dispatchNavigateToNextPage(addPage: boolean = false) {
      this.setCurrentPage(addPage)
    },

    async dispatchGetStockOut(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
      this.loading = true
      this.error = null
      try {
        const { status, data } = await API.stockOut.getStockOut(filters, pagination ?? this.pagination)
        if (status !== HttpStatusCode.Ok) {
          this.records = []
          this.error = 'Unable to load stock out history.'
          return
        }
        this.records = (data.data ?? []) as Record<string, unknown>[]
        const meta =
          data.meta ??
          (data.current_page != null
            ? {
                current_page: data.current_page,
                per_page: data.per_page,
                total: data.total,
                last_page: data.last_page,
              }
            : undefined)
        if (meta) {
          this.setPagination({ meta: meta as any })
        }
      } catch (e: any) {
        this.records = []
        if (e?.response?.status === HttpStatusCode.Unauthorized) {
          await router.push('/login')
          return
        }
        const msg = e?.response?.data?.message
        this.error =
          typeof msg === 'string' && msg.trim() ? msg : 'Unable to load stock out history. Please try again later.'
      } finally {
        this.loading = false
      }
    },
  },
})
